import get from 'lodash/get';
import set from 'lodash/set';
import { createSelector } from 'reselect';
import { EXPORT_CONTROLS } from '@atlassian/cssxp-utils-generic';
import { FeatureFlagKey } from '@atlassian/cssxp-typescommon';
import * as Permissions from './helpers/permissions';
import { SecurityStateTree } from './reducers/security';

const securitySelector = (state: SecurityStateTree) => state.security;

export const userProfileSelector = createSelector(securitySelector, (security) => security.profile);

const permissionsSelector = createSelector(securitySelector, (security) => {
  if (!security?.profile) {
    return [];
  }

  return security?.profile?.permissions;
});

export const featureFlagSelector = createSelector(securitySelector, (security) => {
  if (!security.profile) {
    return {};
  }

  return security.profile.featureFlags;
});

export const getFeatureFlag = (flag: FeatureFlagKey) =>
  createSelector(featureFlagSelector, (featureFlags) => {
    if (!featureFlags) {
      return false;
    }

    return featureFlags[flag] === true;
  });

export const hasValidBitbucketLink = createSelector(securitySelector, (security) =>
  get(security.profile, 'hasValidBitbucketLink', false)
);

export const hasAdminAccess = createSelector(permissionsSelector, Permissions.hasAdminAccess);

export const canSearchUserOrganizations = createSelector(
  securitySelector,
  (securityState) => securityState.isAuthenticated
);

export const canWriteExportControls = createSelector(
  permissionsSelector,
  getFeatureFlag(EXPORT_CONTROLS),
  (permissions, hasFeatureFlag) => Permissions.hasExportControlWriteAccess(permissions) && hasFeatureFlag
);

export const isDealDeskPerson = createSelector(permissionsSelector, (permissions) =>
  Permissions.isDealDeskPerson(permissions)
);

export const isFinancePerson = createSelector(permissionsSelector, (permissions) =>
  Permissions.isFinancePerson(permissions)
);
export const getLoggedInUserRoles = createSelector(securitySelector, (security) => get(security.profile, 'roles', []));

export const roleSelector = createSelector(securitySelector, (security) => {
  const profileMap = {};
  get(security.profile, 'roles', []).forEach((role) => set(profileMap, `${role}`, true));
  return profileMap;
});

export const getAvatarUrl = createSelector(securitySelector, (security) => get(security, 'profile.avatarUrl'));

export const getUserId = createSelector(securitySelector, (security) => get(security, 'profile.userId'));

export const isElevatedAdmin = createSelector(permissionsSelector, (permissions) =>
  Permissions.isElevatedAdmin(permissions)
);
